import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { governanceAPI } from '../lib/governanceApi';
import { useSuperAdminAuth } from './SuperAdminAuthContext';

/**
 * Context for sharing pending approvals count between Sidebar and Approvals page.
 * Enables immediate (optimistic) badge updates when approving/rejecting requests.
 */
const PendingApprovalsContext = createContext({
  pendingCount: 0,
  setPendingCount: () => {},
  decrementCount: () => {},
  refreshCount: () => {},
  isLoading: false,
});

export const usePendingApprovals = () => useContext(PendingApprovalsContext);

export const PendingApprovalsProvider = ({ children }) => {
  const { isAuthenticated } = useSuperAdminAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch the actual count from the API
  const refreshCount = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const res = await governanceAPI.getPendingApprovalCount();
      setPendingCount(res.data?.pending_count || 0);
    } catch (error) {
      console.debug('[PendingApprovalsContext] Could not fetch pending count:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Decrement count by 1 (optimistic update)
  const decrementCount = useCallback(() => {
    setPendingCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Initial fetch and periodic refresh
  useEffect(() => {
    if (isAuthenticated) {
      refreshCount();
      // Refresh every 30 seconds
      const interval = setInterval(refreshCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, refreshCount]);

  const value = {
    pendingCount,
    setPendingCount,
    decrementCount,
    refreshCount,
    isLoading,
  };

  return (
    <PendingApprovalsContext.Provider value={value}>
      {children}
    </PendingApprovalsContext.Provider>
  );
};

export default PendingApprovalsContext;
